// Setting up an authenticator app from Your account (#875), for a person who has none.
//
// **The act is three calls, in this order** (`api/authenticator.ts`): a step-up for
// `mfa_setup`, the setup with its grant, which answers with the secret, and the verify
// with a code from the app, which enables it and answers with the recovery codes. A
// person with no password and no factor has nothing to step up with, and the server
// lets a fresh session stand in (#743), as it does for a first passkey.
//
// **The secret is shown, never kept.** It is drawn as text and as a QR code, never in a
// `title` or an accessible name, never logged or stored, and it leaves the page when the
// setup ends, however it ends.

import { useEffect, useRef, useState } from "react";

import { MFA_SETUP_PURPOSE, type AuthenticatorActions, type Replacement } from "../api/authenticator";
import { ApiError, messageOf } from "../api/http";
import { stepUpFactorsOf, type MfaStatus } from "../api/passkeys";
import { Field, Notice } from "./chrome";
import { qrModules } from "./qrCode";
import { useDropOnPageHide } from "./pageHide";
import { StepUpDialog, useStepUpAttempt } from "./StepUp";

/** A quiet zone of four modules around the code, as the standard asks. */
const QUIET = 4;

/** The setup link, as a QR code the app scans. */
function QrCode({ uri }: { uri: string }) {
  const modules = qrModules(uri);
  const size = modules.length + QUIET * 2;
  const dark = modules
    .flatMap((row, y) =>
      row.map((on, x) => (on ? `M${x + QUIET} ${y + QUIET}h1v1h-1z` : "")),
    )
    .join("");
  return (
    <svg
      className="qr-code"
      role="img"
      aria-label="QR code for your authenticator app"
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="crispEdges"
    >
      <rect className="qr-code__light" width={size} height={size} />
      <path className="qr-code__dark" d={dark} />
    </svg>
  );
}

function Prove({
  actions,
  mfa,
  onStarted,
  onCancel,
}: {
  actions: AuthenticatorActions;
  mfa: MfaStatus;
  onStarted: (setup: Replacement) => void;
  onCancel: () => void;
}) {
  const { proof, attempt, unproven, setProof, spent } = useStepUpAttempt();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const factors = stepUpFactorsOf(mfa);
  const nothingToProve = !factors.passkey && !factors.totp && !factors.password;
  // With no check to fill in, the focus goes to the act that starts the setup; otherwise
  // the check takes it (`autoFocus` below).
  const proceed = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (nothingToProve) proceed.current?.focus();
  }, [nothingToProve]);

  const start = async () => {
    if (!nothingToProve && proof === null) return;
    setProblem(null);
    setBusy(true);
    try {
      const grant = nothingToProve ? null : await actions.stepUp(proof!, MFA_SETUP_PURPOSE);
      onStarted(await actions.beginSetup(grant));
    } catch (failure) {
      setProblem(`${messageOf(failure)} Nothing was set up.`);
    } finally {
      setBusy(false);
      spent();
    }
  };

  return (
    <div className="panel__body stack stack--tight" data-testid="enrol-authenticator">
      <h3>Set up an authenticator app</h3>
      <p className="muted">
        Once the app is set up, signing in asks for a code from it, and every other session of
        yours is signed out, a support session viewing as you included. This one stays signed
        in.
      </p>
      {problem === null ? null : <Notice tone="bad">{problem}</Notice>}
      {nothingToProve ? (
        <p className="muted">
          You have no password or second factor to confirm this with, so your first
          authenticator app can be set up only soon after signing in. If it is refused, sign in
          again and retry.
        </p>
      ) : (
        <StepUpDialog
          key={attempt}
          purpose={MFA_SETUP_PURPOSE}
          factors={factors}
          disabled={busy}
          beginPasskey={actions.beginPasskey}
          onProofChange={setProof}
          onError={setProblem}
          autoFocus
        />
      )}
      <div className="actions">
        <button
          ref={proceed}
          type="button"
          className="btn btn--primary"
          disabled={busy || (!nothingToProve && unproven)}
          onClick={() => void start()}
        >
          {busy ? "Starting…" : "Continue"}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Why a verify ended the setup rather than refusing one code: a setup no longer
 * pending, which lapses ten minutes after it started, or one another session changed
 * while this code was checked, by turning the app on or starting its setup again.
 * Either way the secret on screen finishes nothing, so the person starts again.
 */
function endedBy(failure: unknown): { why: string; elsewhere: boolean } | null {
  if (!(failure instanceof ApiError)) return null;
  if (failure.status === 404) {
    return { why: "This setup has ended. Start it again.", elsewhere: false };
  }
  if (failure.status === 409) {
    return {
      why: "This setup changed in another session: the app was turned on there, or its setup was started again. Start it again here if you still need it.",
      elsewhere: true,
    };
  }
  return null;
}

function Confirm({
  actions,
  setup,
  onDone,
  onEnded,
  onCancel,
}: {
  actions: AuthenticatorActions;
  setup: Replacement;
  onDone: (codes: string[]) => void;
  /** The setup is over and nothing was set up here; `elsewhere` when another session changed it. */
  onEnded: (why: string, elsewhere: boolean) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const field = useRef<HTMLInputElement | null>(null);
  // The field is disabled while the code is checked, which takes the focus from it;
  // a refused code gives it back, so the person types the next one straight away.
  const [refused, setRefused] = useState(0);
  useEffect(() => {
    field.current?.focus();
  }, [refused]);
  // Leaving the page ends the setup as Cancel does, secret and all (#929).
  useDropOnPageHide(onCancel);

  const confirm = async () => {
    setProblem(null);
    setBusy(true);
    try {
      const codes = await actions.verifySetup(code.trim());
      onDone(codes);
    } catch (failure) {
      const ended = endedBy(failure);
      if (ended !== null) {
        onEnded(ended.why, ended.elsewhere);
        return;
      }
      setProblem(`${messageOf(failure)} Nothing was set up; try a new code.`);
      setBusy(false);
      setRefused((n) => n + 1);
      return;
    }
  };

  return (
    <div className="panel__body stack stack--tight" data-testid="enrol-authenticator">
      <h3>Add this key to your authenticator app</h3>
      <p>
        Scan the QR code with your authenticator app, or type the key into it, then enter the
        code it shows.
      </p>
      <QrCode uri={setup.qr_uri} />
      <p>
        <code className="secret">{setup.secret}</code>
      </p>
      <p>
        <a href={setup.qr_uri}>Open in an authenticator app</a>
      </p>
      {problem === null ? null : <Notice tone="bad">{problem}</Notice>}
      <form
        className="stack stack--tight"
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy && code.trim() !== "") void confirm();
        }}
      >
        <Field
          ref={field}
          id="enrol-authenticator-code"
          label="A code from your authenticator app"
          inputMode="numeric"
          autoComplete="one-time-code"
          disabled={busy}
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={busy || code.trim() === ""}>
            {busy ? "Checking…" : "Turn on"}
          </button>
          <button type="button" className="btn" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function Done({ codes, onSaved }: { codes: string[]; onSaved: () => void }) {
  const heading = useRef<HTMLHeadingElement | null>(null);
  // Leaving the page puts the codes away as the button does (#929).
  useDropOnPageHide(onSaved);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  return (
    <div className="panel__body stack stack--tight" data-testid="enrolled-authenticator">
      <h3 ref={heading} tabIndex={-1}>
        Your authenticator app is set up
      </h3>
      <Notice tone="good">
        Signing in now asks for a code from it. Your other sessions were signed out.
      </Notice>
      <p>Your recovery codes. Save them somewhere safe now: they are shown only once.</p>
      <ul className="recovery-codes">
        {codes.map((code) => (
          <li key={code}>
            <code>{code}</code>
          </li>
        ))}
      </ul>
      <div className="actions">
        <button type="button" className="btn btn--primary" onClick={onSaved}>
          I have saved these codes
        </button>
      </div>
    </div>
  );
}

type Stage =
  | { kind: "idle" }
  | { kind: "prove" }
  | { kind: "confirm"; setup: Replacement }
  | { kind: "done"; codes: string[] };

/**
 * The setup, for a person the status read says has no authenticator app. `onEnrolled`
 * re-reads the status; the recovery codes stay on screen until the person puts them
 * away, and `onFinished` is called then.
 */
export function EnrolAuthenticator({
  actions,
  mfa,
  onEnrolled,
  onFinished,
  onChanged,
  onBegin,
}: {
  actions: AuthenticatorActions;
  mfa: MfaStatus;
  onEnrolled: () => void;
  onFinished: () => void;
  /**
   * Another session changed the authenticator, and `why` says so. The panel shows it
   * and reads the status again: if the app is now on, this setup is no longer drawn,
   * so the notice cannot live here.
   */
  onChanged: (why: string) => void;
  /** A setup starts: whatever the panel said about the last one is over. */
  onBegin: () => void;
}) {
  const [stage, setStage] = useState<Stage>({ kind: "idle" });
  /** What the last setup that set nothing up ended with, shown until another starts. */
  const [ended, setEnded] = useState<string | null>(null);
  const offer = useRef<HTMLButtonElement | null>(null);
  const [returning, setReturning] = useState(false);
  useEffect(() => {
    if (returning && stage.kind === "idle") {
      offer.current?.focus();
      setReturning(false);
    }
  }, [returning, stage.kind]);

  const endWith = (why: string) => {
    setStage({ kind: "idle" });
    setEnded(why);
    setReturning(true);
  };

  if (stage.kind === "prove") {
    return (
      <Prove
        actions={actions}
        mfa={mfa}
        onCancel={() => endWith("Nothing was set up.")}
        onStarted={(setup) => setStage({ kind: "confirm", setup })}
      />
    );
  }
  if (stage.kind === "confirm") {
    return (
      <Confirm
        actions={actions}
        setup={stage.setup}
        onCancel={() => endWith("Nothing was set up.")}
        onEnded={(why, elsewhere) => {
          if (!elsewhere) {
            endWith(why);
            return;
          }
          setStage({ kind: "idle" });
          setEnded(null);
          setReturning(true);
          onChanged(why);
        }}
        onDone={(codes) => {
          setStage({ kind: "done", codes });
          onEnrolled();
        }}
      />
    );
  }
  if (stage.kind === "done") {
    return <Done codes={stage.codes} onSaved={onFinished} />;
  }
  return (
    <div className="panel__body stack stack--tight">
      {ended === null ? null : <Notice>{ended}</Notice>}
      <div className="actions">
        <button
          ref={offer}
          type="button"
          className="btn btn--primary"
          onClick={() => {
            setEnded(null);
            onBegin();
            setStage({ kind: "prove" });
          }}
        >
          Set up an authenticator app
        </button>
      </div>
    </div>
  );
}
